import {
  applySnapshot,
  flow,
  getParent,
  Instance,
  types,
} from "mobx-state-tree";
import type { IAnyStateTreeNode } from "mobx-state-tree";
import { SaveSurveyRulesRPC } from "@pulse/pulse-rpcs/lib/survey-rules/saveSurveyRulesRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useSaveSurveyRulesRPCClientImpl } from "../rpcs/RPC";
import type { SurveyRule } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRule";
import { SurveyRuleValidationErrorGroupEnums } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleValidationErrorGroup";
import type { SurveyRuleValidationErrorGroup } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleValidationErrorGroup";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";

type SaveSurveyRulesRPCError = Exclude<
  SaveSurveyRulesRPC.RPCError,
  | SaveSurveyRulesRPC.RPCError.MaxLimitExceeded
  | SaveSurveyRulesRPC.RPCError.InvalidSurveyRules
>;

type SaveSurveyRulesSnackbarState = "SUCCESS" | "VALIDATION_ERROR";

interface SaveSurveyRulesStoreInitialState {
  loadingSaveSurveyRules: boolean;
  saveSurveyRulesRPCError: SaveSurveyRulesRPCError | null;
  maxLimitExceededError: number | null;
  saveSurveyRulesSnackbarState: SaveSurveyRulesSnackbarState | null;
  isProjectAlreadyArchivedErrorDialogVisible: boolean;
  isUnexpectedValidationErrorDialogVisible: boolean;
}

interface SurveyRuleStoreParent extends IAnyStateTreeNode {
  rulesRPCObject: SurveyRule[];
  getSurveyRulesStore: {
    getSurveyRules: (surveyId: string) => void;
  };
  setValidationErrors: (
    validationErrorGroups: SurveyRuleValidationErrorGroup[],
  ) => void;
}

const getInitialState = (): SaveSurveyRulesStoreInitialState => ({
  loadingSaveSurveyRules: false,
  saveSurveyRulesRPCError: null,
  maxLimitExceededError: null,
  saveSurveyRulesSnackbarState: null,
  isProjectAlreadyArchivedErrorDialogVisible: false,
  isUnexpectedValidationErrorDialogVisible: false,
});

const SaveSurveyRulesRPCErrorValues = Object.values(
  SaveSurveyRulesRPC.RPCError,
).filter(
  (rpcError): rpcError is SaveSurveyRulesRPCError =>
    ![
      SaveSurveyRulesRPC.RPCError.MaxLimitExceeded,
      SaveSurveyRulesRPC.RPCError.InvalidSurveyRules,
    ].includes(rpcError),
);

const hasUnexpectedValidationError = (
  validationErrorGroups: SurveyRuleValidationErrorGroup[],
): boolean =>
  validationErrorGroups.some(({ target }) => {
    if (
      !(target instanceof SurveyRuleValidationErrorGroupEnums.Target.Condition)
    ) {
      return false;
    }

    return target.validationErrors.includes(
      SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
        .EXACT_ONE_CONDITION_MUST_HAVE_NONE_CONNECTOR,
    );
  });

export const SaveSurveyRulesStore = types
  .model("SaveSurveyRulesStore", {
    loadingSaveSurveyRules: types.boolean,
    saveSurveyRulesRPCError: types.maybeNull(
      types.enumeration(
        "SaveSurveyRulesRPCError",
        SaveSurveyRulesRPCErrorValues,
      ),
    ),
    maxLimitExceededError: types.maybeNull(types.integer),
    saveSurveyRulesSnackbarState: types.maybeNull(
      types.union(types.literal("SUCCESS"), types.literal("VALIDATION_ERROR")),
    ),
    isProjectAlreadyArchivedErrorDialogVisible: types.boolean,
    isUnexpectedValidationErrorDialogVisible: types.boolean,
  })
  .actions((store) => ({
    saveSurveyRules: flow(function* (surveyId: string) {
      try {
        store.saveSurveyRulesRPCError = null;
        store.maxLimitExceededError = null;
        store.saveSurveyRulesSnackbarState = null;
        store.isProjectAlreadyArchivedErrorDialogVisible = false;
        store.isUnexpectedValidationErrorDialogVisible = false;
        store.loadingSaveSurveyRules = true;

        const surveyRuleStore = getParent<SurveyRuleStoreParent>(store);

        const apiClient = getAPIClient(store);
        const request = new SaveSurveyRulesRPC.Request(
          new LeoUUID(surveyId),
          surveyRuleStore.rulesRPCObject,
        );

        const result: LeoRPCResult<
          SaveSurveyRulesRPC.Response,
          SaveSurveyRulesRPC.Errors.Errors
        > = yield useSaveSurveyRulesRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          store.saveSurveyRulesSnackbarState = "SUCCESS";
          surveyRuleStore.getSurveyRulesStore.getSurveyRules(surveyId);
          return;
        }

        if (
          result.error instanceof SaveSurveyRulesRPC.Errors.MaxLimitExceeded
        ) {
          store.maxLimitExceededError = result.error.maxLimit;
          return;
        }

        if (
          result.error instanceof SaveSurveyRulesRPC.Errors.InvalidSurveyRules
        ) {
          if (hasUnexpectedValidationError(result.error.validationErrorGroup)) {
            store.isUnexpectedValidationErrorDialogVisible = true;
            return;
          }

          surveyRuleStore.setValidationErrors(
            result.error.validationErrorGroup,
          );
          store.saveSurveyRulesSnackbarState = "VALIDATION_ERROR";
          return;
        }

        switch (result.error.code) {
          case SaveSurveyRulesRPC.RPCError.InvalidSurveyRules:
          case SaveSurveyRulesRPC.RPCError.MaxLimitExceeded:
            // These RPC errors are already been handled above
            break;
          case SaveSurveyRulesRPC.RPCError.InvalidSurveyId:
            store.saveSurveyRulesRPCError = result.error.code;
            break;
          case SaveSurveyRulesRPC.RPCError.ProjectAlreadyArchived:
            store.saveSurveyRulesRPCError = result.error.code;
            store.isProjectAlreadyArchivedErrorDialogVisible = true;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingSaveSurveyRules = false;
      }
    }),

    clearErrors: (): void => {
      store.saveSurveyRulesRPCError = null;
      store.maxLimitExceededError = null;
      store.saveSurveyRulesSnackbarState = null;
      store.isProjectAlreadyArchivedErrorDialogVisible = false;
      store.isUnexpectedValidationErrorDialogVisible = false;
    },

    hideMaxLimitExceededSnackbar: (): void => {
      store.maxLimitExceededError = null;
    },

    hideSaveSurveyRulesSnackbar: (): void => {
      store.saveSurveyRulesSnackbarState = null;
    },

    hideProjectAlreadyArchivedErrorDialog: (): void => {
      store.isProjectAlreadyArchivedErrorDialogVisible = false;
    },

    hideUnexpectedValidationErrorDialog: (): void => {
      store.isUnexpectedValidationErrorDialogVisible = false;
    },

    resetStore: (): void => {
      applySnapshot(store, getInitialState());
    },
  }));

export const createSaveSurveyRulesStore = (): Instance<
  typeof SaveSurveyRulesStore
> => SaveSurveyRulesStore.create(getInitialState());
